import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { Modal } from '@/components/shared/Modal';
import { Button } from '@/components/ui/button';

export interface FinalizeCounts {
  unresolved: number;
  fail: number;
  conflict: number;
  warning: number;
}

interface BulkImportFinalizeDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
  counts: FinalizeCounts;
}

/**
 * Phase 2f-F (§9): destructive confirmation — Finalize permanently abandons
 * every remaining unresolved row on a `PARTIALLY_COMPLETED` session (§21,
 * D-066 item 10). Never framed as a substitute for Final Import.
 */
export function BulkImportFinalizeDialog({
  open,
  onClose,
  onConfirm,
  isPending,
  counts,
}: BulkImportFinalizeDialogProps) {
  return (
    <Modal
      open={open}
      onClose={isPending ? () => {} : onClose}
      title="Finalize Import — Abandon Remaining Rows"
    >
      <div className="space-y-4">
        <div className="flex items-start gap-3 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3">
          <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 h-4 w-4 text-red-700" />
          <div className="text-sm text-red-800">
            <p className="font-medium">This action is permanent and cannot be undone.</p>
            <p className="mt-1 text-xs">
              Finalize is not a substitute for Final Import. Use it only when the remaining rows
              below are not expected to become eligible (e.g. they require workbook data that will
              never be available).
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
          <div className="rounded-lg border border-border bg-muted/30 px-3 py-2">
            <p className="text-xs text-muted-foreground">Unresolved</p>
            <p className="text-lg font-semibold text-foreground">{counts.unresolved}</p>
          </div>
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2">
            <p className="text-xs text-red-800">Fail</p>
            <p className="text-lg font-semibold text-red-800">{counts.fail}</p>
          </div>
          <div className="rounded-lg border border-purple-500/30 bg-purple-500/10 px-3 py-2">
            <p className="text-xs text-purple-800">Conflict</p>
            <p className="text-lg font-semibold text-purple-800">{counts.conflict}</p>
          </div>
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2">
            <p className="text-xs text-amber-800">Warning</p>
            <p className="text-lg font-semibold text-amber-800">{counts.warning}</p>
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          All {counts.unresolved} unresolved row(s) above will be permanently marked abandoned for
          this session. This session will reach Completed. No further correction, revalidation, or
          Final Import will be possible for this session afterward.
        </p>

        <div className="flex justify-end gap-3 border-t border-border pt-4">
          <Button variant="ghost" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={isPending}>
            {isPending && (
              <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3.5 w-3.5 animate-spin" />
            )}
            Finalize — Abandon Remaining Rows
          </Button>
        </div>
      </div>
    </Modal>
  );
}
